import { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
export function useUndoRedoHistory(options = {}) {
    const { capacity = 100, coalesceMs = 250, isEqual = _.isEqual, clone = _.cloneDeep, } = options;
    const stateRef = useRef({ stack: [], index: -1 });
    const [, forceRender] = useState(0);
    const notify = () => forceRender((v) => v + 1);
    const commit = (next) => {
        const state = stateRef.current;
        const current = state.index >= 0 ? state.stack[state.index] : undefined;
        if (current !== undefined && isEqual(current, next)) {
            return;
        }
        // If we edited after undo, drop the future.
        const baseStack = state.index < state.stack.length - 1
            ? state.stack.slice(0, state.index + 1)
            : state.stack;
        let newStack = [...baseStack, clone(next)];
        let newIndex = baseStack.length;
        // Enforce capacity by dropping oldest.
        if (newStack.length > capacity) {
            const overflow = newStack.length - capacity;
            newStack = newStack.slice(overflow);
            newIndex = Math.max(0, newIndex - overflow);
        }
        stateRef.current = { stack: newStack, index: newIndex };
        notify();
    };
    const debouncedCommit = useMemo(() => {
        return _.debounce((next) => commit(next), coalesceMs, {
            leading: false,
            trailing: true,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coalesceMs, capacity]);
    useEffect(() => {
        return () => {
            debouncedCommit.cancel();
        };
    }, [debouncedCommit]);
    const reset = (initial) => {
        debouncedCommit.cancel();
        stateRef.current = { stack: [clone(initial)], index: 0 };
        notify();
    };
    const clear = () => {
        debouncedCommit.cancel();
        stateRef.current = { stack: [], index: -1 };
        notify();
    };
    const undo = () => {
        debouncedCommit.flush();
        const state = stateRef.current;
        if (state.index <= 0)
            return null;
        const nextIndex = state.index - 1;
        stateRef.current = { stack: state.stack, index: nextIndex };
        notify();
        return clone(state.stack[nextIndex]);
    };
    const redo = () => {
        debouncedCommit.flush();
        const state = stateRef.current;
        if (state.index < 0 || state.index >= state.stack.length - 1)
            return null;
        const nextIndex = state.index + 1;
        stateRef.current = { stack: state.stack, index: nextIndex };
        notify();
        return clone(state.stack[nextIndex]);
    };
    const peek = () => {
        const state = stateRef.current;
        if (state.index < 0)
            return null;
        return clone(state.stack[state.index]);
    };
    const push = (next) => {
        debouncedCommit(next);
    };
    const state = stateRef.current;
    const canUndo = state.index > 0;
    const canRedo = state.index >= 0 && state.index < state.stack.length - 1;
    return {
        reset,
        push,
        undo,
        redo,
        clear,
        canUndo,
        canRedo,
        size: state.stack.length,
        index: state.index,
        peek,
    };
}
